import {
  bestMedal,
  createDefaultProfile,
  medalForTime,
  type Medal,
  type MedalThresholds,
  type PhaseId,
  type PlayerProfile,
  type GameSettings,
} from './model';
import type { ProgressRepository } from './repository';

export interface FinishUpdate {
  medal: Medal;
  isNewBest: boolean;
  bestTimeMs: number;
  bestLapMs: number | null;
  totalMs: number;
}

export class ProgressService {
  degraded = false;

  constructor(
    private readonly repo: ProgressRepository,
    private profile: PlayerProfile,
  ) {}

  get kind(): ProgressRepository['kind'] {
    return this.repo.kind;
  }

  get data(): PlayerProfile {
    return this.profile;
  }

  get settings(): GameSettings {
    return this.profile.settings;
  }

  isCompleted(id: PhaseId): boolean {
    return this.profile.phases[id]?.completed === true;
  }

  async persist(): Promise<void> {
    this.profile.updatedAt = new Date().toISOString();
    try {
      await this.repo.save(this.profile);
    } catch (error) {
      console.warn('Falha ao salvar progresso', error);
      this.degraded = true;
    }
  }

  async recordAttempt(id: PhaseId): Promise<void> {
    const phase = this.profile.phases[id];
    phase.attempts += 1;
    phase.lastPlayedAt = new Date().toISOString();
    await this.persist();
  }

  async recordLap(id: PhaseId, lapMs: number): Promise<void> {
    const phase = this.profile.phases[id];
    if (phase.bestLapMs == null || lapMs < phase.bestLapMs) {
      phase.bestLapMs = lapMs;
      await this.persist();
    }
  }

  async recordFinish(id: PhaseId, totalMs: number, bestLapMs: number | null, thresholds: MedalThresholds): Promise<FinishUpdate> {
    const phase = this.profile.phases[id];
    const medal = medalForTime(totalMs, thresholds);
    const isNewBest = phase.bestTimeMs == null || totalMs < phase.bestTimeMs;
    phase.completed = true;
    phase.finishes += 1;
    phase.medal = bestMedal(phase.medal, medal);
    phase.lastPlayedAt = new Date().toISOString();
    if (isNewBest) phase.bestTimeMs = totalMs;
    if (bestLapMs != null && (phase.bestLapMs == null || bestLapMs < phase.bestLapMs)) {
      phase.bestLapMs = bestLapMs;
    }
    await this.persist();
    return {
      medal: phase.medal,
      isNewBest,
      bestTimeMs: phase.bestTimeMs ?? totalMs,
      bestLapMs: phase.bestLapMs,
      totalMs,
    };
  }

  async updateSettings(partial: Partial<GameSettings>): Promise<void> {
    this.profile.settings = { ...this.profile.settings, ...partial };
    await this.persist();
  }

  async reset(): Promise<void> {
    const graphics = this.profile.settings;
    try {
      await this.repo.reset();
    } catch {
      this.degraded = true;
    }
    this.profile = createDefaultProfile();
    this.profile.settings = graphics;
    await this.persist();
  }
}
